import React, { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CircularProgress, Alert } from '@mui/material'
import { useSendPhoneVerificationMutation, useVerifyPhoneMutation } from 'src/redux/api'
import MyModal from 'src/components/ui/modal/MyModal'
import { CloseIcon } from '@svgs'
import { Button, InputField } from 'src/components/ui'
import { extractErrorMessage } from 'src/helpers'

interface PhoneVerificationModalProps {
  open: boolean
  handleClose: () => void
  phoneNumber: string
  onVerificationSuccess: () => void
}

const PhoneVerificationModal: FC<PhoneVerificationModalProps> = ({
  open,
  handleClose,
  phoneNumber,
  onVerificationSuccess,
}) => {
  const { t } = useTranslation()
  const [sendPhoneCode, { isLoading: isSending, error: sendError }] =
    useSendPhoneVerificationMutation()
  const [verifyPhone, { isLoading: isVerifying }] = useVerifyPhoneMutation()

  const [codeSent, setCodeSent] = useState(false)
  const [verificationCode, setVerificationCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const handleSendCode = async () => {
    try {
      setError(null)
      await sendPhoneCode({ phoneNumber }).unwrap()
      setCodeSent(true)
    } catch (err) {
      console.error('[phone-verification] sendPhoneCode failed', err)
      const fallback = t('phone_verification.send_error', 'Failed to send verification code')
      setError(extractErrorMessage(err, fallback))
    }
  }

  const handleVerifyCode = async () => {
    try {
      setError(null)
      await verifyPhone({ phoneNumber, code: verificationCode }).unwrap()
      setSuccess(t('phone_verification.success', 'Phone number verified successfully'))

      // Wait a moment to show success message before closing
      setTimeout(() => {
        handleClose()
        onVerificationSuccess()
      }, 1500)
    } catch (err) {
      console.error('[phone-verification] verifyPhone failed', err)
      const fallback = t('phone_verification.verify_error', 'Invalid verification code')
      setError(extractErrorMessage(err, fallback))
    }
  }

  // Send code automatically when modal opens
  useEffect(() => {
    if (open && !codeSent && !isSending && !sendError) {
      handleSendCode()
    }
  }, [open, codeSent, isSending, sendError, phoneNumber])

  return (
    <MyModal
      showClose={false}
      open={open}
      handleClose={handleClose}
      width="w-[calc(100%_-_2rem)] md:w-[26rem]"
      contentClassName="!p-6 !px-8"
    >
      <>
        <button
          onClick={handleClose}
          className="icon-button fill-danger absolute z-20 top-4 right-4 w-4 h-4 hover:fill-danger-strong"
          disabled={isSending || isVerifying}
        >
          <CloseIcon />
        </button>

        <div className="flex flex-col gap-4">
          <span className="text-center font-bold">{t('phone_verification.title')}</span>

          {error && (
            <Alert severity="error">
              <span className="">{error}</span>
            </Alert>
          )}

          {success && (
            <Alert severity="success">
              <span>{success}</span>
            </Alert>
          )}

          <span className="text-center mb-4">
            {codeSent
              ? t('phone_verification.code_sent', { phoneNumber })
              : t('phone_verification.verify_phone')}
          </span>

          {!codeSent ? (
            <div className="flex flex-col gap-4">
              <Button onClick={handleSendCode} disabled={isSending} className="w-full">
                {isSending ? <CircularProgress size={24} /> : t('phone_verification.send_code')}
              </Button>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <InputField
                label={t('phone_verification.verification_code')}
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
              />

              <Button onClick={handleVerifyCode} disabled={isVerifying || !verificationCode}>
                {isVerifying ? <CircularProgress size={24} /> : t('phone_verification.verify_code')}
              </Button>

              <Button onClick={handleSendCode} disabled={isSending}>
                {isSending ? <CircularProgress size={16} /> : t('phone_verification.resend_code')}
              </Button>
            </div>
          )}
        </div>
      </>
    </MyModal>
  )
}

export default PhoneVerificationModal
